import React, { useState } from "react";
import { LEAD_STATUS_FILTER_OPTIONS, PRIORITY_FILTER_OPTIONS } from '../config';

const FilterBar = ({ filters, setFilters, setTaskFilterModalOpen }) => {
  const [refFilterOpen, setRefFilterOpen] = useState(false);
  const [tempRefKey, setTempRefKey] = useState(filters.refFilter?.key || '');
  const [tempRefValue, setTempRefValue] = useState(filters.refFilter?.value || '');
  
  const handleFilterChange = (key, value) => {
    // Map component keys to filter state properties
    const keyMapping = {
      'searchText': 'searchText',
      'status': 'status',
      'priority': 'priority'
    };
    
    setFilters(prev => ({
      ...prev,
      [keyMapping[key] || key]: value
    }));
  };

  const handleNestedFilterChange = (parentKey, key, value) => {
    setFilters(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [key]: value
      }
    }));
  };
  
  const handleApplyRefFilter = () => {
    setFilters(prev => ({
      ...prev,
      refFilter: {
        key: tempRefKey.trim(),
        value: tempRefValue.trim()
      }
    }));
    setRefFilterOpen(false);
  };
  
  const handleClearRefFilter = () => {
    setTempRefKey('');
    setTempRefValue('');
    setFilters(prev => ({
      ...prev,
      refFilter: { key: '', value: '' }
    }));
    setRefFilterOpen(false);
  };

  // Find the filter options that use "all" as the value (FilterBar uses "all" instead of "")
  const statusFilterOptions = LEAD_STATUS_FILTER_OPTIONS.filter(option => 
    option.value === "all" || option.value !== "");
  
  const priorityFilterOptions = PRIORITY_FILTER_OPTIONS.filter(option => 
    option.value === "all" || option.value !== "");
    
  const isRefFilterActive = filters.refFilter && filters.refFilter.key !== '';

  return (
    <div className="bg-white p-4 mb-6 rounded-lg shadow-sm">
      <div className="flex flex-wrap gap-4 items-center">
        {/* Search field */}
        <div className="flex-grow min-w-[200px]">
          <label htmlFor="search" className="sr-only">Search</label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              id="search"
              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
              placeholder="Search leads..."
              value={filters.searchText}
              onChange={(e) => handleFilterChange("searchText", e.target.value)}
            />
          </div>
        </div>

        {/* Status filter */}
        <div className="w-48">
          <label htmlFor="status" className="block text-xs font-medium text-gray-700 mb-1">Status</label>
          <select
            id="status"
            className="block w-full py-2 pl-3 pr-10 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            {statusFilterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Priority filter */}
        <div className="w-40">
          <label htmlFor="priority" className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
          <select
            id="priority"
            className="block w-full py-2 pl-3 pr-10 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            value={filters.priority}
            onChange={(e) => handleFilterChange("priority", e.target.value)}
          >
            {priorityFilterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Task filter button */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Task Filters</label>
          <button
            type="button"
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => setTaskFilterModalOpen(true)}
          >
            <svg className="mr-1.5 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
            Task Filters
          </button>
        </div>
        
        {/* Ref filter button */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Ref Data Filter</label>
          <button
            type="button"
            className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isRefFilterActive 
                ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
            onClick={() => setRefFilterOpen(true)}
          >
            <svg className="mr-1.5 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.5 3a.5.5 0 01.5.5v9a.5.5 0 01-.5.5h-13a.5.5 0 01-.5-.5v-9a.5.5 0 01.5-.5h13zm-13-1A1.5 1.5 0 000 3.5v9A1.5 1.5 0 001.5 14h13a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0014.5 2h-13z" clipRule="evenodd" />
              <path d="M7 5.5a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5a.5.5 0 01-.5-.5zm-1.5 2a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5a.5.5 0 01-.5-.5zm-1.5 2a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5a.5.5 0 01-.5-.5z" />
            </svg>
            Ref Filter
            {isRefFilterActive && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Active
              </span>
            )}
          </button>
        </div>

        {/* Date range pickers (Created At) */}
        <div className="flex space-x-2">
          <div>
            <label htmlFor="startDate" className="block text-xs font-medium text-gray-700 mb-1">Created Start</label>
            <input
              type="date"
              id="startDate"
              className="border rounded px-2 py-1 text-sm"
              value={filters.dateRange.start}
              onChange={(e) => handleNestedFilterChange("dateRange", "start", e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-xs font-medium text-gray-700 mb-1">Created End</label>
            <input
              type="date"
              id="endDate"
              className="border rounded px-2 py-1 text-sm"
              value={filters.dateRange.end}
              onChange={(e) => handleNestedFilterChange("dateRange", "end", e.target.value)}
            />
          </div>
        </div>

        {/* Date range pickers (Incident Date) */}
        <div className="flex space-x-2">
          <div>
            <label htmlFor="incidentStartDate" className="block text-xs font-medium text-gray-700 mb-1">Incident Start</label>
            <input
              type="date"
              id="incidentStartDate"
              className="border rounded px-2 py-1 text-sm"
              value={filters.incidentDateRange.start}
              onChange={(e) => handleNestedFilterChange("incidentDateRange", "start", e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="incidentEndDate" className="block text-xs font-medium text-gray-700 mb-1">Incident End</label>
            <input
              type="date"
              id="incidentEndDate"
              className="border rounded px-2 py-1 text-sm"
              value={filters.incidentDateRange.end}
              onChange={(e) => handleNestedFilterChange("incidentDateRange", "end", e.target.value)}
            />
          </div>
        </div>

        {/* Reset filters button */}
        <div className="mt-2 self-end">
          <button
            type="button"
            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            onClick={() => setFilters({
              dateRange: { start: '', end: '' },
              incidentDateRange: { start: '', end: '' },
              searchText: '',
              priority: 'all',
              specificTasks: [],
              status: 'all',
              refFilter: { key: '', value: '' }
            })}
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Active filters indicators */}
      {(filters.searchText ||
        filters.status !== "all" ||
        filters.priority !== "all" ||
        filters.dateRange.start ||
        filters.dateRange.end ||
        filters.incidentDateRange.start ||
        filters.incidentDateRange.end ||
        filters.specificTasks.length > 0 ||
        isRefFilterActive) && (
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="text-xs text-gray-500">Active filters:</span>
          
          {filters.searchText && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              Search: {filters.searchText}
              <button 
                type="button" 
                className="ml-1 text-blue-500 hover:text-blue-700"
                onClick={() => handleFilterChange("searchText", "")}
              >
                &times;
              </button>
            </span>
          )}
          
          {filters.status !== "all" && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
              Status: {filters.status.replace('_', ' ')}
              <button 
                type="button" 
                className="ml-1 text-purple-500 hover:text-purple-700"
                onClick={() => handleFilterChange("status", "all")}
              >
                &times;
              </button>
            </span>
          )}
          
          {filters.priority !== "all" && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              Priority: {filters.priority}
              <button 
                type="button" 
                className="ml-1 text-yellow-500 hover:text-yellow-700"
                onClick={() => handleFilterChange("priority", "all")}
              >
                &times;
              </button>
            </span>
          )}
          
          {/* Ref filter badge */}
          {isRefFilterActive && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
              Ref: {filters.refFilter.key}
              {filters.refFilter.value && ` = ${filters.refFilter.value}`}
              <button 
                type="button" 
                className="ml-1 text-indigo-500 hover:text-indigo-700"
                onClick={handleClearRefFilter}
              >
                &times;
              </button>
            </span>
          )}
          
          {filters.dateRange.start && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              Created From: {filters.dateRange.start}
              <button 
                type="button" 
                className="ml-1 text-green-500 hover:text-green-700"
                onClick={() => handleNestedFilterChange("dateRange", "start", "")}
              >
                &times;
              </button>
            </span>
          )}
          
          {filters.dateRange.end && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              Created To: {filters.dateRange.end}
              <button 
                type="button" 
                className="ml-1 text-green-500 hover:text-green-700"
                onClick={() => handleNestedFilterChange("dateRange", "end", "")}
              >
                &times;
              </button>
            </span>
          )}
          
          {filters.incidentDateRange.start && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
              Incident From: {filters.incidentDateRange.start}
              <button 
                type="button" 
                className="ml-1 text-teal-500 hover:text-teal-700"
                onClick={() => handleNestedFilterChange("incidentDateRange", "start", "")}
              >
                &times;
              </button>
            </span>
          )}
          
          {filters.incidentDateRange.end && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
              Incident To: {filters.incidentDateRange.end}
              <button 
                type="button" 
                className="ml-1 text-teal-500 hover:text-teal-700"
                onClick={() => handleNestedFilterChange("incidentDateRange", "end", "")}
              >
                &times;
              </button>
            </span>
          )}
          
          {filters.specificTasks.length > 0 && filters.specificTasks.map((taskFilter, idx) => (
            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
              Task: {taskFilter.name} ({taskFilter.complete ? 'Completed' : 'Pending'})
              <button 
                type="button" 
                className="ml-1 text-indigo-500 hover:text-indigo-700"
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    specificTasks: prev.specificTasks.filter((_, i) => i !== idx)
                  }));
                }}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
      
      {/* Ref Filter Modal */}
      {refFilterOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Filter by Reference Data
                    </h3>
                    <div className="mt-2 space-y-4">
                      <div>
                        <label htmlFor="refKey" className="block text-sm font-medium text-gray-700">
                          Key
                        </label>
                        <input
                          type="text"
                          id="refKey"
                          value={tempRefKey}
                          onChange={(e) => setTempRefKey(e.target.value)}
                          placeholder="Enter key name..."
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="refValue" className="block text-sm font-medium text-gray-700">
                          Value (optional)
                        </label>
                        <input
                          type="text"
                          id="refValue"
                          value={tempRefValue}
                          onChange={(e) => setTempRefValue(e.target.value)}
                          placeholder="Enter value... (optional)"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                      <div className="text-sm text-gray-500 mt-2">
                        <p>Filter leads by reference data fields. Specify a key to find leads containing that key in their ref data.</p>
                        <p className="mt-1">Optionally specify a value to match against that key.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button 
                  type="button" 
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${!tempRefKey.trim() ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}`}
                  onClick={handleApplyRefFilter}
                  disabled={!tempRefKey.trim()}
                >
                  Apply Filter
                </button>
                <button 
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setRefFilterOpen(false)}
                >
                  Cancel
                </button>
                {isRefFilterActive && (
                  <button 
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-red-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={handleClearRefFilter}
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar; 